// Kanban de deals — /crm/deals/kanban (Story 11.5)
// Board drag-and-drop (@dnd-kit) com move otimista + rollback (AC4/AC5/AC6).
// Filtros (responsável/saas/origem/faixa de valor/parado há +X dias) persistidos
// em URL query params (AC7/AC10). Toggle lista↔kanban preservando filtros (AC10).
// RBAC: admin/comercial arrastam; financeiro/suporte veem read-only (AC8).
// Skeleton de colunas no loading (AC9).

import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { List, RefreshCw } from 'lucide-react';

import type { CrmDealStageSchema, SaleOriginSchema } from '@control-center/shared';

import { KanbanBoard } from '../../../components/crm/deals/KanbanBoard';
import { PillButton } from '../../../components/clients/PillButton';
import { Select } from '../../../components/clients/form-primitives';
import { OPEN_STAGES, TERMINAL_STAGES } from '../../../lib/deal-helpers';
import { useCrmDealsKanban, type CrmDealsKanbanFilters } from '../../../hooks/use-crm-deals';
import { useUsers } from '../../../hooks/use-users';
import { useSaasProducts } from '../../../hooks/use-saas-products';
import { useCrmPermissions } from '../../../hooks/use-crm-permissions';
import { extractErrorMessage } from '../../../lib/errors';
import type { CrmDealEntity } from '../../../lib/crm-deals-api';

const ORIGIN_OPTIONS: { value: '' | SaleOriginSchema; label: string }[] = [
  { value: '', label: 'Todas as origens' },
  { value: 'inbound', label: 'Inbound' },
  { value: 'outbound', label: 'Outbound' },
  { value: 'referral', label: 'Indicação' },
  { value: 'self_service', label: 'Self-service' },
  { value: 'migration', label: 'Migração' },
];

const STALLED_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Qualquer tempo no estágio' },
  { value: '7', label: 'Parado há +7 dias' },
  { value: '14', label: 'Parado há +14 dias' },
  { value: '30', label: 'Parado há +30 dias' },
];

/** Lê os filtros da URL (fonte única — navegação com voltar funciona, AC7). */
function readFilters(params: URLSearchParams): {
  filters: CrmDealsKanbanFilters;
  stalledDays?: number;
} {
  const filters: CrmDealsKanbanFilters = {};
  const responsible = params.get('responsible_user_id');
  const saas = params.get('saas_product_id');
  const origin = params.get('origin');
  const valueMin = params.get('value_min');
  const valueMax = params.get('value_max');
  const stalled = params.get('stalled_days');

  if (responsible) filters.responsible_user_id = responsible;
  if (saas) filters.saas_product_id = saas;
  if (origin) filters.origin = origin as SaleOriginSchema;
  if (valueMin && !Number.isNaN(Number(valueMin))) filters.value_min = Number(valueMin);
  if (valueMax && !Number.isNaN(Number(valueMax))) filters.value_max = Number(valueMax);

  const stalledDays = stalled && Number(stalled) > 0 ? Number(stalled) : undefined;
  if (stalledDays !== undefined) filters.stalled_days = stalledDays;

  return { filters, stalledDays };
}

export function CrmDealsKanbanPage(): JSX.Element {
  const navigate = useNavigate();
  const { canWrite: canDrag } = useCrmPermissions();

  const [searchParams, setSearchParams] = useSearchParams();
  const { filters, stalledDays } = useMemo(() => readFilters(searchParams), [searchParams]);

  const {
    data: kanban,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  } = useCrmDealsKanban(filters);
  const { data: users } = useUsers();
  const { data: saasProducts } = useSaasProducts({ limit: 100 });

  const usersById = useMemo(() => {
    const map = new Map<string, string>();
    for (const u of users ?? []) map.set(u.id, u.name);
    return map;
  }, [users]);

  // Atualiza 1 filtro na URL preservando os demais (AC7).
  const setFilter = (key: string, value: string): void => {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    setSearchParams(next, { replace: false });
  };

  const clearFilters = (): void => setSearchParams(new URLSearchParams(), { replace: false });

  const hasActiveFilters = Array.from(searchParams.keys()).length > 0;

  // Preserva filtros ao alternar pra lista (AC10).
  const goToList = (): void => {
    const qs = searchParams.toString();
    navigate(qs ? `/crm/deals?${qs}` : '/crm/deals');
  };

  const handleOpenDeal = (deal: CrmDealEntity): void => navigate(`/crm/deals/${deal.id}`);

  return (
    <div className="flex flex-col gap-5">
      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="m-0 text-[30px] font-bold leading-none tracking-[-0.025em] text-ink">
            Funil de deals
          </h1>
          <p className="mt-[6px] max-w-[560px] text-[13px] leading-[1.5] text-muted">
            Arraste cada negociação pelos estágios do funil — o status atualiza na hora.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <PillButton onClick={goToList} icon={<List size={14} strokeWidth={2} />}>
            Vista em lista
          </PillButton>
          <PillButton
            onClick={() => refetch()}
            icon={
              <RefreshCw
                size={14}
                strokeWidth={2}
                className={isFetching ? 'animate-spin' : undefined}
              />
            }
          >
            Atualizar
          </PillButton>
        </div>
      </header>

      {/* Filtros (AC7) */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-[1fr_1fr_1fr_180px_180px]">
        <Select
          aria-label="Filtrar por responsável"
          value={filters.responsible_user_id ?? ''}
          onChange={(e) => setFilter('responsible_user_id', e.target.value)}
        >
          <option value="">Todos os responsáveis</option>
          {(users ?? []).map((u) => (
            <option key={u.id} value={u.id}>
              {u.name}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Filtrar por produto SaaS"
          value={filters.saas_product_id ?? ''}
          onChange={(e) => setFilter('saas_product_id', e.target.value)}
        >
          <option value="">Todos os produtos</option>
          {(saasProducts?.items ?? []).map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </Select>
        <Select
          aria-label="Filtrar por origem"
          value={filters.origin ?? ''}
          onChange={(e) => setFilter('origin', e.target.value)}
        >
          {ORIGIN_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
        <input
          type="number"
          min={0}
          inputMode="numeric"
          value={filters.value_min ?? ''}
          onChange={(e) => setFilter('value_min', e.target.value)}
          placeholder="Valor mín."
          aria-label="Valor mínimo"
          className="h-10 w-full rounded-[10px] border border-line bg-panel px-3 text-[13px] text-ink placeholder:text-muted-3 outline-none transition-colors focus:border-line-2"
        />
        <input
          type="number"
          min={0}
          inputMode="numeric"
          value={filters.value_max ?? ''}
          onChange={(e) => setFilter('value_max', e.target.value)}
          placeholder="Valor máx."
          aria-label="Valor máximo"
          className="h-10 w-full rounded-[10px] border border-line bg-panel px-3 text-[13px] text-ink placeholder:text-muted-3 outline-none transition-colors focus:border-line-2"
        />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Select
          aria-label="Filtrar por tempo no estágio"
          value={filters.stalled_days?.toString() ?? ''}
          onChange={(e) => setFilter('stalled_days', e.target.value)}
          className="md:w-[260px]"
        >
          {STALLED_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
        {hasActiveFilters && <PillButton onClick={clearFilters}>Limpar filtros</PillButton>}
      </div>

      {/* Conteúdo */}
      {isLoading ? (
        <KanbanSkeleton />
      ) : isError || !kanban ? (
        <ErrorState
          message={extractErrorMessage(error, 'Não conseguimos carregar o funil.')}
          onRetry={() => refetch()}
        />
      ) : (
        <KanbanBoard
          kanban={kanban}
          filters={filters}
          usersById={usersById}
          canDrag={canDrag}
          stalledDays={stalledDays}
          onOpenDeal={handleOpenDeal}
        />
      )}
    </div>
  );
}

// =============================================================================
// Estados
// =============================================================================

function KanbanSkeleton(): JSX.Element {
  const columns = [...OPEN_STAGES, ...TERMINAL_STAGES] as CrmDealStageSchema[];
  return (
    <div className="flex gap-3 overflow-x-auto pb-2">
      {columns.map((stage) => (
        <div
          key={stage}
          className="flex w-[280px] shrink-0 flex-col rounded-[14px] border border-line bg-panel"
        >
          <div className="border-b border-line px-3 py-[10px]">
            <div className="h-3 w-28 animate-pulse rounded bg-panel-2" />
          </div>
          <div className="flex flex-col gap-2 p-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-[88px] animate-pulse rounded-[10px] bg-panel-2" />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function ErrorState({ message, onRetry }: { message: string; onRetry: () => void }): JSX.Element {
  return (
    <div className="rounded-[14px] border border-bad/40 bg-panel p-6">
      <h2 className="text-[15px] font-semibold tracking-[-0.005em] text-ink">
        Não conseguimos carregar o funil.
      </h2>
      <p className="mt-1 text-[12px] text-muted">{message}</p>
      <div className="mt-4">
        <PillButton onClick={onRetry} icon={<RefreshCw size={14} strokeWidth={2} />}>
          Tentar de novo
        </PillButton>
      </div>
    </div>
  );
}
